import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { api, apiErrorMessage } from '../../services/api'
import { Button, Input, Field, ErrorText, Spinner } from '../../components/ui'
import { Logo } from '../../components/brand/Logo'
import { Icon } from '../../components/ui/icons'

interface InviteInfo {
  valid: boolean
  email?: string
  firstName?: string
  lastName?: string
}

/**
 * Activación de cuenta invitada (portal de empresa o de paciente): valida
 * el enlace de invitación y permite crear la contraseña (token de un solo uso).
 */
export function ActivatePage({ portal = 'empresa' }: { portal?: 'empresa' | 'paciente' }) {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const loginPath = portal === 'empresa' ? '/empresa/login' : '/paciente/login'
  const subtitle = portal === 'empresa' ? 'Activación de cuenta · Portal de empresas' : 'Activación de cuenta · Portal de pacientes'
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => (await axios.get<InviteInfo>(`/api/auth/invite/${encodeURIComponent(token!)}`)).data,
    enabled: Boolean(token),
    retry: false,
  })

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (password !== confirm) {
      setError('Las contraseñas no coinciden')
      return
    }
    setSaving(true)
    try {
      await api.post('/auth/set-password', { token, password })
      setDone(true)
      setTimeout(() => navigate(loginPath), 2500)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-900 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl">
        <h1 className="mb-1">
          {/* El logo vuelve al sitio público (pedido de Chejo, minuta 15-09). */}
          <Link to="/" title="Ir al sitio de Cima Sport">
            <Logo className="h-11" />
          </Link>
        </h1>
        <p className="mb-6 text-sm text-gray-500">{subtitle}</p>

        {invite.isLoading && <Spinner />}

        {!invite.isLoading && (!invite.data?.valid || invite.isError) && (
          <div className="space-y-4">
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              El enlace de activación es inválido o expiró. Pide a Cima Sport que reenvíe la invitación.
            </p>
            <p className="text-center text-xs text-gray-400">
              <Link to="/" className="hover:underline">← Volver al sitio</Link>
            </p>
          </div>
        )}

        {invite.data?.valid && !done && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="rounded-lg bg-soft px-3 py-2 text-sm">
              Hola <strong>{invite.data.firstName}</strong>, crea la contraseña para <span className="font-mono text-xs">{invite.data.email}</span>
            </p>
            <Field label="Nueva contraseña" hint="Mínimo 8 caracteres, con letras y números">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoFocus />
            </Field>
            <Field label="Repite la contraseña">
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </Field>
            <ErrorText>{error}</ErrorText>
            <Button type="submit" variant="cta" className="w-full py-2.5" disabled={saving || password.length < 8}>
              {saving ? 'Guardando…' : 'Activar cuenta'}
            </Button>
          </form>
        )}

        {done && (
          <div className="space-y-3 text-center">
            <p className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-3 text-sm font-medium text-ink">
              <Icon name="check" className="h-4 w-4 shrink-0 text-emerald-600" />
              Cuenta activada. Te llevamos al inicio de sesión…
            </p>
            <Link to={loginPath} className="text-sm font-medium text-brand-700 hover:underline">Ir a iniciar sesión</Link>
          </div>
        )}
      </div>
    </div>
  )
}
